import { RollbackOutlined, SaveOutlined } from "@ant-design/icons";
import {
  Breadcrumb,
  Button,
  Col,
  message,
  Modal,
  Row,
  Space,
  Tabs,
} from "antd";
import { RootState } from "app.store";
import { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { CustomTheme } from "shared/components/custom-theme/custom-theme";
import { LanguageSelector } from "shared/components/language-selector";
import { Seo } from "shared/components/seo/seo";
import {
  DefaultErrorMessage,
  UpdateDataSuccessfully,
} from "shared/constants/define-localization";
import useRefresh from "shared/hooks/use-refresh";
import { SeoModel } from "shared/models/seo-model";
import { t, CoreResource } from "shared/plugins/localization";
import { setLoading } from "states/global-value/global-value.slice";
import {
  addStaticPage,
  getStaticPageById,
  updateStaticPage,
} from "states/static-page/static-page.api";
import {
  CreateStaticPageModel,
  StaticPageModel,
  UpdatedStaticPageModel,
} from "states/static-page/static-page.models";
import { StaticPageInformationTab } from "./components/staticpage-information-tab";

interface RouteParams {
  staticPageId: string;
}
interface IAddUpdateStaticPageProps {}

export function AddUpdateStaticPage(props: IAddUpdateStaticPageProps) {
  const refresh = useRefresh();
  const { staticPageId } = useParams<RouteParams>();
  const [model, setModel] = useState<StaticPageModel>({} as StaticPageModel);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const clientLanguages = useSelector(
    (state: RootState) => state.localization.clientLanguages,
    shallowEqual
  );
  const [currentLanguage, setCurrentLanguage] = useState<string>();

  const dispatch = useDispatch();
  const getStaticPage = async () => {
    try {
      const res = await getStaticPageById(Number(staticPageId));

      setModel(res.data);
    } catch (error) {
      message.error(
        error?.response?.data?.error?.message ??
          t(CoreResource, DefaultErrorMessage)
      );
    }
  };

  const onSaved = async () => {
    dispatch(setLoading(true));
    try {
      if (isUpdateMode) {
        const payload = {
          id: model.id,
          isCustomizePage: model.isCustomizePage,
          pageContent: model.pageContent,
          seoFields: model.seoFields,
          customizeTheme: model?.customizeTheme,
          title: model.title,
          slug: model.slug,
        } as UpdatedStaticPageModel;
        await updateStaticPage(payload, currentLanguage);
      } else {
        const payload = {
          isCustomizePage: model.isCustomizePage,
          pageContent: model.pageContent,
          seoFields: model.seoFields,
          title: model.title,
          slug: model.slug,
          customizeTheme: model?.customizeTheme,
        } as CreateStaticPageModel;
        await addStaticPage(payload, currentLanguage);
      }
      message.success(t(CoreResource, UpdateDataSuccessfully));
      refresh();
    } catch (error) {
      message.error(
        error?.response?.data?.error?.message ??
          t(CoreResource, DefaultErrorMessage)
      );
    }
    dispatch(setLoading(false));
  };

  useEffect(() => {
    if (clientLanguages.length > 0 && !currentLanguage) {
      setCurrentLanguage(clientLanguages.find((m) => m.isDefault)?.langCode);
    }
  }, [clientLanguages]);

  useEffect(() => {
    if (document.location.href.indexOf("/add") > -1) {
      setIsUpdateMode(false);
    } else {
      setIsUpdateMode(true);
      getStaticPage();
    }
  }, []);

  return (
    <div className="add-update-static-page">
      <Row gutter={[16, 16]} className="mb-4">
        <Col flex="auto">
          <Breadcrumb className="mb-4">
            <Breadcrumb.Item>
              <Link to="/admin/static-page">Trang tĩnh </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              {isUpdateMode
                ? "Cập nhật trang " + model.title
                : "Thêm trang tĩnh"}
            </Breadcrumb.Item>
          </Breadcrumb>
        </Col>
        <Col flex="auto" className="d-flex justify-content-end">
          <Space>
            {isUpdateMode && (
              <LanguageSelector
                onChange={async (value) => {
                  const res = await getStaticPageById(model.id, value);
                  if (!res.data.title) {
                    Modal.confirm({
                      title: "Thông báo",
                      content:
                        "Nội dung chưa được khởi tạo cho ngôn ngữ này, bạn có muốn khởi tạo?",
                      onOk: () => {
                        setCurrentLanguage(value);
                      },
                    });
                    return;
                  } else {
                    setCurrentLanguage(value);
                    setModel(res.data);
                  }
                }}
                selectedCode={currentLanguage}
              />
            )}
            <Link to="/admin/static-page">
              <Button type="default" icon={<RollbackOutlined />}>
                Quay lại
              </Button>
            </Link>
            <Button onClick={onSaved} type="primary" icon={<SaveOutlined />}>
              Lưu
            </Button>
          </Space>
        </Col>
      </Row>
      <Tabs type="card">
        <Tabs.TabPane tab="Thông tin cơ bản" key="1">
          <StaticPageInformationTab
            model={model}
            onChange={setModel}
            isUpdateMode={isUpdateMode}
          />
        </Tabs.TabPane>
        <Tabs.TabPane tab="Giao diện" key="giao-dien">
          <CustomTheme
            onChange={(changedModel) => {
              setModel({ ...model, customizeTheme: changedModel });
            }}
            model={model.customizeTheme ?? {}}
          />
        </Tabs.TabPane>
        <Tabs.TabPane tab="SEO" key="seo">
          <Seo
            model={model.seoFields ?? ({} as SeoModel)}
            slug={model.slug}
            onChange={(seoFields, slug) => {
              setModel({ ...model, slug, seoFields });
            }}
          />
        </Tabs.TabPane>
      </Tabs>
    </div>
  );
}
